import moment from 'moment';
import { getTimeFormat } from '../utils/dates';
import { getMonthlyEvents, getEvent } from '../utils/apiEvents';
import {
  LOAD_CALENDAR_EVENTS,
  SHOW_DAILY_EVENT,
  SHOW_DETAIL_EVENT,
  HIDE_DETAIL_EVENT,
  GO_TO_TODAY
} from '../constants/actionTypes/events';

/**
 * Show the card with the events for the selected day
 * @param {function} dispatch - Dispatcher for the reducer
 * @param {object} slot - Slot selected in the calendar
 */
export function showDailyEvent(dispatch, slot) {
  dispatch({
    type: SHOW_DAILY_EVENT,
    selectedDate: new Date(slot.slots[0]),
    dayDetail: `${moment(slot.slots[0]).format('YYYY-MM-DD')}`
  });
}

/**
 * Hide the view of the detail of an event
 * @param {function} dispatch - Dispatcher for the reducer
 */
export function hideDetailEvent(dispatch) {
  dispatch({ type: HIDE_DETAIL_EVENT });
}

/**
 * Show the card with the details of the event.
 * @param {function} dispatch - Dispatcher for the reducer
 * @param {EventCalendarModel} event - Event to show into the detailed view
 * @param {string} [dateDay] - Date to show in the detail
 */
export function showDetailEvent(dispatch, event, dateDay = null) {
  getEvent(event.id).then(eventDetail => {
    dispatch({
      type: SHOW_DETAIL_EVENT,
      detail: {
        id: event.id,
        eventName: event.title,
        eventDate: dateDay || event.otherDays[0],
        eventConvener: event.eventCaller ? event.eventCaller.name : null,
        authorVisible: event.authorVisible,
        eventLocation: eventDetail.eventAddress.addressName,
        status: event.status,
        docEvent: eventDetail.file.fileName,
        docEventPath: eventDetail.file.clientPath,
        descriptionEvent: event.subtitle,
        linkNews: eventDetail.linkNews,
        subscribable: eventDetail.subscribable,
        eventType: eventDetail.eventType,
        onlyInternal: eventDetail.onlyInternal,
        startDate: getTimeFormat(new Date(eventDetail.startDate)),
        closeDate: getTimeFormat(new Date(eventDetail.closeDate)),
        eventDays: event.hoursArray.map(date => ({
          eventDay: date.date,
          eventHour: date.hours
            .map(
              hour =>
                `${getTimeFormat(hour.startTime)} - ${getTimeFormat(
                  hour.endTime
                )}`
            )
            .join('/')
        }))
      }
    });
  });
}

export const showDirectDetailEvent = (dispatch, event) => {
  dispatch({
    type: SHOW_DETAIL_EVENT,
    detail: {
      id: event.id,
      eventName: event.title,
      eventDate: `${moment(event.startDate).format('YYYY-MM-DD')}`,
      eventConvener: event.eventCaller ? event.eventCaller.name : null,
      authorVisible: event.authorVisible,
      eventLocation: event.eventAddress?.addressName,
      status: event.status,
      docEvent: event.file?.fileName,
      docEventPath: event.file?.clientPath,
      descriptionEvent: event.subtitle,
      linkNews: event.linkNews,
      startDate: getTimeFormat(new Date(event.startDate)),
      closeDate: getTimeFormat(new Date(event.closeDate)),
      eventDays: event?.eventDays?.map(date => ({
        eventDay: date.day,
        eventHour: date.eventDayTimes
          .map(
            hour =>
              `${getTimeFormat(hour.startTime)} - ${getTimeFormat(
                hour.endTime
              )}`
          )
          .join('/')
      }))
    }
  });
};

/**
 * Get all the info about dates and hours of each event.
 * The returned event will have the next template:
 *
 * hours: {
 *  eventId: [
 *    {
 *      date: Date of this event {string}
 *      hours: [
 *        Range of hours of this event for that date {string}
 *      ]
 *    }
 *  ]
 * }
 * @param {object} events - Events gathered from back
 */
function getHours(events) {
  const hours = {};
  Object.keys(events).forEach(date => {
    events[date].forEach(ev => {
      // Create an object for each ID
      // If it exists, just push the info
      if (hours[ev.eventId]) {
        hours[ev.eventId].push({
          date,
          hours: ev.eventDayTimes ? [...ev.eventDayTimes] : []
        });
      } else {
        hours[ev.eventId] = [
          {
            date,
            hours: ev.eventDayTimes ? [...ev.eventDayTimes] : []
          }
        ];
      }
    });
  });
  return hours;
}

/**
 * Load events to be displayed in the calendar. If an event starts after/before
 * the month and year, won't be retrieved. This method will format the data using
 * the next template:
 *
 * events: [
 *   {
 *      title: Title of the event {string}
 *      subtitle: Description of the event {string}
 *      eventCaller: {
 *        id: ID of the convener {string}
 *        name: Name of the convener {string}
 *      }
 *      status: Status of the event {string}
 *      otherDays: [
 *        Dates of this event {string}
 *      ]
 *      id: ID of the event {string}
 *      start: Date where this event starts {Date}
 *      end: Date where this event ends {Date}
 *      eventAddress: {
 *        addressName: Place where te event is going to be
 *      }
 *      hoursArray: [
 *        {
 *          date: Date of the following array of hours {string}
 *          hours: [
 *            Range of hours of this event for that date {string}
 *          ]
 *        }
 *      ]
 *   }
 * ]
 * @param {function} dispatch - Dispatcher for the reducer
 * @param {string} [year] - Year of the events to retrieve
 * @param {string} [month] - Month of the events to retrieve
 * @param {Date} []
 */

export const loadCalendarEvents = (
  dispatch,
  fromDate,
  toDate,
  dayDetail = null
  // _daySelected = null
) =>
  new Promise(resolved => {
    const eventsObj = [];
    const firstMonthDay = new Date(
      fromDate.getFullYear(),
      fromDate.getMonth(),
      1
    );
    getMonthlyEvents(firstMonthDay, toDate).then(events => {
      const hours = getHours(events);
      // Filter events by id
      Object.keys(events).forEach(date => {
        const ids = [];
        events[date].forEach(ev => {
          if (!ids.includes(ev.eventId)) {
            // Create a new event with the needed info
            const newEvent = {
              ...ev,
              otherDays: ev.otherDays ? ev.otherDays : [],
              id: ev.eventId,
              start: new Date(date),
              end: new Date(date),
              hoursArray: hours[ev.eventId]
            };
            ids.push(ev.eventId);
            newEvent.otherDays.unshift(date);
            if (ev.eventDayTimes) {
              if (ev.eventDayTimes.length) {
                eventsObj.push(newEvent);
              }
            } else {
              eventsObj.push(newEvent);
            }
          }
        });
      });
      const today = new Date();
      dispatch({
        type: LOAD_CALENDAR_EVENTS,
        events: eventsObj,
        eventsByDay: { ...events },
        dayDetail: dayDetail
          ? `${moment(dayDetail).format('YYYY-MM-DD')}`
          : `${moment(new Date()).format('YYYY-MM-DD')}`,
        selectedDate:
          fromDate.getMonth() === today.getMonth()
            ? new Date(fromDate).setDate(today.getDate())
            : new Date(fromDate)
      });

      resolved(eventsObj);
    });
  });

/**
 * Moves calendar date and daily info to the current day
 * @param {function} dispatch - Dispatcher for the reducer
 * @param {Date} currentSelectedDate - Selected date in the reducer
 */
export function goToToday(dispatch, currentSelectedDate) {
  const fromDate = new Date();
  const toDate = new Date();
  toDate.setMonth(toDate.getMonth() + 1);

  if (currentSelectedDate.getMonth() !== fromDate.getMonth()) {
    loadCalendarEvents(dispatch, fromDate, toDate);
  }
  dispatch({
    type: GO_TO_TODAY,
    selectedDate: fromDate,
    dayDetail: `${moment(fromDate).format('YYYY-MM-DD')}`
  });
}
